import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import assetStore from '../AssetStore';
import FilePreview, { FilePreviewFileInfo, FilePreviewFileInfoType } from '../FilePreview';
import { FileType, OperateType, SyncSourceType } from '../common/type';
import style from './index.module.scss';
import FileCard from '../FileCard';
import Sync2AdsManagerBtn from '../Sync2AdsManagerBtn';
import { MAxiosResponse } from '@ad/ad_utils/src/utils/mAxios';
import { checkSuccessResponse } from '@ad/ad_utils/src/utils/common';
import { Breadcrumb, Checkbox, Tooltip } from '@byte-design/ui';
import Loading from '../../Loading';
import moment from 'moment';
import { marginRightPx } from '../common/const';
import UploadBar from '../UploadBar';
import { Download } from '@icon-park/react';
import type { AdvInfo } from '../Send2AdsManager';
import { AdvAssetsAuthResult } from '../AuthManageConfirmModal/type';

interface SharingProps {
  shareLink: string;
  apis: {
    getSharedAssetsList: (req: {
      shareToken: string;
      page: number;
      limit: number;
      folderId?: string;
    }) => Promise<MAxiosResponse>;
    getAssetSharingMetaData: (req: { shareToken: string }) => Promise<MAxiosResponse>;
    getFolderTree?: (req: { shareToken: string }) => Promise<MAxiosResponse>;
    getAdvertiserList?: Function;
    sendVideoToTtam?: (req: {
      fileIds: string[];
      advIds: string[];
      shareToken: string;
      permissionType?: AdvAssetsAuthResult;
    }) => Promise<MAxiosResponse<any>>;
  };
  store?: any; // 设置用户身份信息
  sendCustomEvent?: Function;
  onSearchAdv?: (advId: string) => Promise<AdvInfo[] | undefined>;
}

const Sharing = (props: SharingProps) => {
  const { shareLink, apis, store, sendCustomEvent, onSearchAdv } = props;
  const shareToken = shareLink.split('/').pop()!;
  const [sharingMetaData, setSharingMetaData] = useState<{
    totalAssetNumber: number;
    partnerName: string;
    createdAt: number;
  }>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      const res = await apis.getFolderTree?.({
        shareToken
      });
      const folderList = [
        {
          name: assetStore.translations.allFiles || 'All files',
          folderId: 'root',
          folderList: res?.data.folderList || []
        }
      ] as any;
      assetStore.setFolderList(folderList);
      assetStore.setSelectFolderId('root');
    })();
  }, []);

  useEffect(() => {
    sendCustomEvent?.('asset_management_sharing_page_show', {
      isLogin: store.loggedIn,
      shareToken
    });
  }, [shareToken, store.loggedIn]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await apis.getAssetSharingMetaData({ shareToken });
      if (checkSuccessResponse(res.code)) {
        setSharingMetaData(res.data);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    assetStore.setLogApi(sendCustomEvent);
  }, [sendCustomEvent]);

  const [previewList, setPreviewList] = useState<FilePreviewFileInfo[]>([]);

  useEffect(() => {
    (async () => {
      assetStore.setShowDataLoading(true);
      const res = await apis.getSharedAssetsList({
        shareToken,
        folderId: assetStore.selectFolderId,
        page: 1,
        limit: 100
      });
      if (checkSuccessResponse(res.code)) {
        const { files, folders } = res.data;
        assetStore.setFileList([
          ...(folders || []).map((folder) => ({
            ...folder,
            fileId: folder.folderId,
            fileType: FileType.Folder
          })),
          ...(files || [])
        ]);
      }
      assetStore.setShowDataLoading(false);
    })();
  }, [assetStore.selectFolderId]);

  function transferFileType(fileType: FileType) {
    if (fileType === FileType.Image) {
      return FilePreviewFileInfoType.IMAGE;
    }
    if (fileType === FileType.Video) {
      return FilePreviewFileInfoType.VIDEO;
    }
    return FilePreviewFileInfoType.OTHER;
  }

  const onPreviewClose = useCallback(() => {
    assetStore.setShowPreview(false);
    setPreviewList([]);
  }, []);

  const renderIcon = (icon, translations, fn) => (
    <div className={style.operateMargin}>
      <Tooltip content={translations} placement="bottom">
        <div className={style.operate} onClick={fn}>
          {icon}
        </div>
      </Tooltip>
    </div>
  );

  const selectVideos = useMemo(
    () => assetStore.selectFileList.filter((item) => item.fileType === FileType.Video),
    [assetStore.selectFileList]
  );

  useEffect(() => {
    assetStore.setOnlyForView(true);
    const list = assetStore.fileList
      .filter((file) => assetStore.canShowPreview(file))
      .map((f) => {
        const { fileId, name, fileUrl, fileType } = f;

        return {
          type: transferFileType(fileType),
          url: fileUrl,
          name,
          id: fileId,
          actions:
            fileType !== FileType.Video
              ? []
              : [
                  {
                    icon: (
                      <Sync2AdsManagerBtn
                        selectedVideos={[f]}
                        shareToken={shareToken}
                        store={store}
                        getAdvertiserList={apis.getAdvertiserList}
                        sendVideoToTtam={apis.sendVideoToTtam}
                        sendCustomEvent={sendCustomEvent}
                        source={SyncSourceType.fromPreview}
                        canManuallyAddAdv
                        onSearchAdv={onSearchAdv}
                      />
                    ),
                    type: 'sync2AdsManager'
                  }
                ]
        };
      });
    setPreviewList(list);
  }, [assetStore.showPreview, assetStore.fileList, assetStore.onlyForView, store.loggedIn, shareToken]);

  const showDownload = useMemo(() => {
    if (assetStore.selectFileList.length === 0) {
      return false;
    }

    const hasFolder = assetStore.selectFileList.filter((v) => v.fileType === FileType.Folder);
    return hasFolder.length === 0;
  }, [assetStore.selectFileList]);

  const selectCheckboxTitle = useMemo(() => {
    if (assetStore.selectFileList.length === 0) {
      return assetStore.translations.selectAllFiles;
    }
    if (assetStore.selectFileList.length > 1) {
      return `${assetStore.selectFileList.length} ${assetStore.translations.items} ${assetStore.translations.haveBeenSelect}`;
    }
    return `${assetStore.selectFileList.length} ${assetStore.translations.item} ${assetStore.translations.haveBeenSelect}`;
  }, [assetStore.selectFileList, assetStore.translations]);

  const selectFileCheckbox = useMemo(
    () =>
      !!assetStore.selectFileList.length && assetStore.selectFileList.length === assetStore.fileList.length,
    [assetStore.selectFileList, assetStore.fileList]
  );

  const selectFileCheckboxIndeterminate = useMemo(
    () =>
      assetStore.selectFileList.length > 0 && assetStore.selectFileList.length !== assetStore.fileList.length,
    [assetStore.selectFileList, assetStore.fileList]
  );

  const selectAllValueChange = useCallback(() => {
    if (assetStore.selectFileList.length < assetStore.fileList.length) {
      assetStore.setSelectFileList([...assetStore.fileList]);
    } else {
      assetStore.setSelectFileList([]);
    }
  }, [assetStore.selectFileList, assetStore.fileList]);

  const renderFileBody = useCallback(
    () => (
      <div className={style.renderBodyContainer}>
        <div className={style.contentHeader}>
          <div className={style.titleFolder}>
            <div>
              <Checkbox
                indeterminate={selectFileCheckboxIndeterminate}
                checked={selectFileCheckbox}
                onValueChange={selectAllValueChange}
              />
            </div>
            <div className={style.title}>{selectCheckboxTitle}</div>
          </div>
        </div>
        <div className={style.content}>
          {assetStore.fileList.map((file) => (
            <FileCard key={file.fileId} file={file} enableDisplayPermissionStatus={false} />
          ))}
        </div>
      </div>
    ),
    [assetStore.fileList, selectCheckboxTitle, selectFileCheckbox, selectFileCheckboxIndeterminate]
  );

  const syncBtnDisabled = useMemo(
    () =>
      assetStore.selectFileList.some((item) => item.fileType !== FileType.Video) ||
      assetStore.selectFileList.length === 0,
    [assetStore.selectFileList]
  );

  const nav = useMemo(
    () => (
      <Breadcrumb onClick={(folderId: string) => assetStore.setSelectFolderId(folderId)}>
        {(assetStore.folderNav || []).map((item) => (
          <Breadcrumb.Item id={item?.folderId} key={item?.folderId}>
            {item?.name}
          </Breadcrumb.Item>
        ))}
      </Breadcrumb>
    ),
    [assetStore.folderNav]
  );

  const handleDownload = useCallback(() => {
    assetStore.selectFileList.forEach((file) => {
      assetStore.downloadFile(file);
    });
    assetStore.sendLog('asset_management_download_files_click', {
      fileCount: assetStore.selectFileList.length,
      type: OperateType.batch
    });
  }, [assetStore.selectFileList, assetStore.downloadFile]);

  return loading ? (
    <Loading loading />
  ) : (
    <DndProvider backend={HTML5Backend}>
      <div className={style.sharing}>
        <Loading loading={assetStore.showDataLoading} />
        <div className={style.body}>
          <div className={style.header}>
            <div className={style.title}>
              {assetStore.translations.sharingPageTitle
                ?.replace('{{count}}', String(sharingMetaData?.totalAssetNumber))
                ?.replace('{{name}}', String(sharingMetaData?.partnerName)) ||
                `${sharingMetaData?.totalAssetNumber} items shared by ${sharingMetaData?.partnerName}`}
              <div className={style.time}>
                <span>{moment.unix(sharingMetaData?.createdAt!).format('YYYY-MM-DD HH:mm:ss')}</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div className={style.headerOperation}>
                <div className={style.operateIcon}>
                  {showDownload && renderIcon(<Download />, assetStore.translations.download, handleDownload)}
                </div>
              </div>
              <Tooltip
                content={
                  syncBtnDisabled
                    ? assetStore.translations.syncDisabledHint || 'Only videos can be synced to ads manager'
                    : null
                }
                mode="white"
                placement="bottom"
              >
                <Sync2AdsManagerBtn
                  disabled={syncBtnDisabled}
                  selectedVideos={selectVideos}
                  shareToken={shareToken}
                  store={store}
                  getAdvertiserList={apis.getAdvertiserList}
                  sendVideoToTtam={apis.sendVideoToTtam}
                  sendCustomEvent={sendCustomEvent}
                  source={SyncSourceType.fromNavBar}
                  canManuallyAddAdv
                  onSearchAdv={onSearchAdv}
                />
              </Tooltip>
            </div>
          </div>
          <div
            style={{
              marginTop: '24px'
            }}
          >
            {nav}
            {renderFileBody()}
          </div>
          {assetStore.showPreview && previewList.length > 0 && (
            <FilePreview
              show={assetStore.showPreview}
              fileList={previewList}
              onClose={onPreviewClose}
              defaultIndex={previewList.findIndex((item) => item.id === assetStore.previewSelectId)}
            />
          )}
          <div
            className={style.uploadBar}
            style={{
              marginRight: assetStore.isDetailDrawerVisible ? marginRightPx : '0'
            }}
          >
            <UploadBar />
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default observer(Sharing);
